// TS twin of iOS `DragStrip.swift`.
// Vertical pointer-drag strip. Emits onDrag(pixelDelta * sensitivity) on
// every move; when currentAngle is provided, the strip displays "X.X°"
// (degrees) in place of the up/down arrow hint.

import { useCallback, useRef, useState } from 'react'
import type { PointerEvent, ReactNode } from 'react'

interface DragStripProps {
  /** Stable hook for tests — labels are copy and must not be matched on. */
  testId?: string
  icon: ReactNode
  label: string
  sensitivity: number
  onDrag: (delta: number) => void
  /** Optional current angle in DEGREES (caller converts from radians). */
  currentAngle?: number | null
  compact?: boolean
}

export function DragStrip({
  icon,
  label,
  sensitivity,
  onDrag,
  currentAngle = null,
  compact = false,
  testId,
}: DragStripProps) {
  const [isDragging, setIsDragging] = useState(false)
  const lastY = useRef<number | null>(null)
  const onDragRef = useRef(onDrag)
  onDragRef.current = onDrag

  const stripWidth = compact ? 48 : 54
  const stripHeight = 66

  const handleDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
    lastY.current = e.clientY
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])

  const handleMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (lastY.current == null) return
      // iOS sign convention: pixel delta = (last - current), so dragging UP
      // (current < last) produces a positive delta.
      const pixelDelta = lastY.current - e.clientY
      lastY.current = e.clientY
      onDragRef.current(pixelDelta * sensitivity)
    },
    [sensitivity],
  )

  const handleEnd = useCallback((e: PointerEvent<HTMLDivElement>) => {
    setIsDragging(false)
    lastY.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  const activeColor = isDragging ? 'cyan' : 'rgba(255, 255, 255, 0.7)'

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <span style={{ fontSize: 14, color: activeColor }}>{icon}</span>
      <div
        role="slider"
        aria-label={`${label} (drag vertically)`}
        aria-orientation="vertical"
        aria-valuemin={-1}
        aria-valuemax={1}
        aria-valuenow={currentAngle ?? 0}
        tabIndex={0}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleEnd}
        onPointerCancel={handleEnd}
        style={{
          width: stripWidth,
          height: stripHeight,
          background: isDragging
            ? 'rgba(0, 255, 255, 0.2)'
            : 'rgba(255, 255, 255, 0.1)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: activeColor,
          touchAction: 'none',
          userSelect: 'none',
          cursor: 'ns-resize',
        }}
      >
        {currentAngle != null ? (
          <span
            style={{
              fontSize: compact ? 11 : 12,
              fontWeight: 600,
              fontFamily: 'ui-monospace, monospace',
            }}
          >
            {currentAngle.toFixed(1)}°
          </span>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              color: 'rgba(255, 255, 255, 0.3)',
              fontSize: 8,
              fontWeight: 700,
            }}
          >
            <span>▲</span>
            <span>▼</span>
          </div>
        )}
      </div>
      <span
        style={{
          fontSize: 10,
          fontWeight: 500,
          color: isDragging ? 'cyan' : 'rgba(255, 255, 255, 0.5)',
        }}
      >
        {label}
      </span>
    </div>
  )
}
